import { withQuery } from 'ufo'
import { useRegistryScript } from '#nuxt-scripts/utils'
import type { RegistryScriptInput } from '#nuxt-scripts/types'
import { object, string, optional, boolean } from '#nuxt-scripts-validator'

export const GoogleRecaptchaOptions = object({
  siteKey: string(),
  // Use enterprise.js instead of api.js
  enterprise: optional(boolean()),
  // Use recaptcha.net (works in China)
  recaptchaNet: optional(boolean()),
  // Language code
  hl: optional(string())
})

export type GoogleRecaptchaInput = RegistryScriptInput<
  typeof GoogleRecaptchaOptions
>

export interface GoogleRecaptchaApi {
  grecaptcha: {
    ready: (callback: () => void) => void
    execute: (siteKey: string, options: { action: string }) => Promise<string>
    enterprise?: {
      ready: (callback: () => void) => void
      execute: (siteKey: string, options: { action: string }) => Promise<string>
    }
  }
}

declare global {
  interface Window {
    grecaptcha?: GoogleRecaptchaApi['grecaptcha']
    ___grecaptcha_cfg?: {
      fns?: Array<() => void>
    }
  }
}

export function useScriptGoogleRecaptcha<T extends GoogleRecaptchaApi>(
  _options?: GoogleRecaptchaInput
) {
  return useRegistryScript<T, typeof GoogleRecaptchaOptions>(
    _options?.key || 'googleRecaptcha',
    (options) => {
      const baseUrl = options?.recaptchaNet
        ? 'https://www.recaptcha.net/recaptcha'
        : 'https://www.google.com/recaptcha'
      const scriptPath = options?.enterprise ? 'enterprise.js' : 'api.js'

      return {
        scriptInput: {
          src: withQuery(`${baseUrl}/${scriptPath}`, {
            render: options?.siteKey,
            hl: options?.hl
          }),
          crossorigin: false
        },
        schema: import.meta.dev ? GoogleRecaptchaOptions : undefined,
        scriptOptions: {
          use() {
            return {
              grecaptcha: window.grecaptcha as GoogleRecaptchaApi['grecaptcha']
            } as T
          }
        },
        clientInit: import.meta.server
          ? undefined
          : () => {
              const w = window
              const readyFn = function (cb: () => void) {
                const cfg = (w.___grecaptcha_cfg = w.___grecaptcha_cfg || {})
                cfg.fns = (cfg.fns || []).concat([cb])
              }

              const stubExecute = (
                _siteKey: string,
                _options: { action: string }
              ): Promise<string> =>
                Promise.reject(new Error('reCAPTCHA has not loaded yet'))

              const g = (w.grecaptcha ||= {
                ready: readyFn,
                execute: stubExecute
              })

              g.ready ||= readyFn
              // Enterprise mode uses grecaptcha.enterprise.ready
              if (options?.enterprise) {
                const e = (g.enterprise ||= {
                  ready: readyFn,
                  execute: stubExecute
                })
                e.ready ||= readyFn
              }
            }
      }
    },
    _options
  )
}
